// src/pages/projects.js

import React, { useMemo } from 'react';
import { graphql, Link, navigate } from 'gatsby';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import styled from 'styled-components';
import { Layout } from '@components';
import ProjectCard, { ProjectGrid } from '@components/projectcard';
import TagFilter from '@components/tagfilter';

/* Built to the same plan as /blogs — breadcrumb, heading, one line of
   explanation, a filter row, then the results — so the two index pages read as
   one pair rather than two unrelated designs. The column is 1000px rather than
   the blog list's 700px because it matches the #projects section on the home
   page, which is where these cards already live. */
const Section = styled.main`
  .inner {
    max-width: 1000px;
    margin: 0 auto;
    margin-top: -1rem;
  }

  .controls {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: var(--space-4);
    margin: var(--space-6) 0 var(--space-5);
  }

  .filter-group {
    display: flex;
    align-items: center;
    gap: var(--space-3);
  }

  /* Optically centred against the filter trigger, which carries the site's
     asymmetric padding; a blockified flex item is centred by its line box and
     would otherwise ride 0.17em high. */
  .clear {
    position: relative;
    top: var(--optical);
    padding: 0;
    background-color: transparent;
    border: 0;
    color: var(--text-muted);
    font-family: var(--font-sans);
    font-size: var(--fz-md);
    line-height: 1;
    cursor: pointer;
    transition: var(--transition);

    &:hover,
    &:focus-visible {
      color: var(--accent);
      outline: none;
    }
  }

  .result-count {
    position: relative;
    top: var(--optical);
    color: var(--text-muted);
    font-size: var(--fz-md);
  }

  .empty {
    padding: var(--space-8) 0;
    color: var(--text-muted);
    text-align: center;
  }
`;

const ProjectsPage = ({ data, location }) => {
  const projects = data.projects.edges.map(({ node }) => node.frontmatter);

  const selected = new URLSearchParams(location.search || '').get('tag');

  const { tags, counts } = useMemo(() => {
    const byTag = {};
    projects.forEach(p => (p.tags || []).forEach(t => (byTag[t] = (byTag[t] || 0) + 1)));
    return {
      tags: Object.keys(byTag).sort((a, b) => byTag[b] - byTag[a] || a.localeCompare(b)),
      counts: { total: projects.length, byTag },
    };
  }, [projects]);

  const visible = selected ? projects.filter(p => (p.tags || []).includes(selected)) : projects;

  const onSelect = tag => {
    const url = tag ? `${location.pathname}?tag=${encodeURIComponent(tag)}` : location.pathname;
    navigate(url, { replace: true });
  };

  return (
    <Layout location={location}>
      <Helmet title="Projects" />
      <Section>
        <div className="inner">
          <span className="breadcrumb">
            <Link to="/">
              <span className="arrow">&larr; Back</span>
            </Link>
          </span>
          <h1 className="small-heading">Projects</h1>
          <p className="subtitle">
            A running list of what I’ve built and worked on, newest first. Open one for the tools,
            dates, and the longer story.
          </p>

          <div className="controls">
            <div className="filter-group">
              <TagFilter tags={tags} counts={counts} selected={selected} onSelect={onSelect} />
              {selected && (
                <button type="button" className="clear" onClick={() => onSelect(null)}>
                  Clear
                </button>
              )}
            </div>
            <span className="result-count">
              {visible.length} {visible.length === 1 ? 'project' : 'projects'}
            </span>
          </div>

          {visible.length > 0 ? (
            <ProjectGrid>
              {visible.map(project => (
                <ProjectCard key={project.slug} project={project} />
              ))}
            </ProjectGrid>
          ) : (
            <p className="empty">Nothing here yet under that topic.</p>
          )}
        </div>
      </Section>
    </Layout>
  );
};

ProjectsPage.propTypes = {
  location: PropTypes.object.isRequired,
  data: PropTypes.shape({
    projects: PropTypes.shape({
      edges: PropTypes.arrayOf(PropTypes.object),
    }),
  }).isRequired,
};

export default ProjectsPage;

export const pageQuery = graphql`
  {
    projects: allMarkdownRemark(
      filter: { fileAbsolutePath: { regex: "/content/projects/" } }
      sort: { fields: [frontmatter___date], order: DESC }
    ) {
      edges {
        node {
          frontmatter {
            title
            slug
            description
            tags
            github
            external
            instagram
            ios
            android
          }
        }
      }
    }
  }
`;
